// File: src/features/jobs/mcpApi.ts
// ParaLex MCP API Integration for Frontend Job Management
//
// ✅ PROVIDES: MCP-enhanced job operations, monitoring, and analysis
// ✅ MAINTAINS: Consistent API patterns with existing jobApi.ts
// ✅ ENABLES: Multi-AI orchestration and comprehensive job insights

import { JobDetail } from './jobTypes';

// ===== MCP-ENHANCED INTERFACES =====

export interface ChatMcpRequest {
  message: string;
  realmId: string;
  userId: string;
  sessionId?: string;
  mcpStrategy?: 'auto' | 'local_only' | 'multi_ai' | 'cost_optimized';
  includeSystemContext?: boolean;
}

export interface ChatMcpResponse {
  response: string;
  confidence: number;
  aiProvidersUsed: string[];
  executionTime: number;
  cost: number;
  metadata: {
    strategy: string;
    sessionId: string;
    timestamp: string;
    mcpEnabled: boolean;
  };
}

export interface JobMcpMetrics {
  active: number;
  waiting: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

export interface DocumentJobMcpMetrics {
  totalDocuments: number;
  documentsProcessingToday: number;
  avgProcessingTimeMs: number;
  successRate: number;
  failureRate: number;
  mostCommonFailure?: string;
  activeDocumentJobs: number;
  completedDocumentJobs: number;
  failedDocumentJobs: number;
}

export interface JobMcpOperationResult {
  success: boolean;
  message: string;
  jobId?: string;
  operation: string;
  timestamp: string;
}

export interface SystemMonitorMcpData {
  mcpHealth: Record<string, boolean>;
  orchestrationStats: {
    totalOrchestrations: number;
    successRate: number;
    avgExecutionTime: number;
    costEfficiency: number;
  };
  aiProviderStatus: Record<string, {
    available: boolean;
    responseTime: number;
    successRate: number;
    cost: number;
  }>;
  recentOrchestrations: any[];
  jobMetrics: JobMcpMetrics;
  documentMetrics: DocumentJobMcpMetrics;
  agentActivity: any[];
}

export interface JobMonitorMcpData {
  enhancedAnalysis: boolean;
  orchestrationResults?: any;
  aiConsensus?: {
    agreement: number;
    recommendations: string[];
    confidence: number;
  };
  toolsExecuted: string[];
  remediationActions: Array<{
    tool: string;
    action: string;
    status: 'pending' | 'completed' | 'failed';
    result?: any;
  }>;
  jobDetail: JobDetail;
  relatedJobs: JobDetail[];
  traceData?: any;
  schemaValidation?: any;
}

export interface McpHealthStatus {
  status: string;
  service: string;
  version: string;
  timestamp: string;
  mcpHealth: {
    initialized: boolean;
    mcpClient: boolean;
    mcpEngine: boolean;
    hasAiProviders: boolean;
  };
  capabilities: {
    jobManagement: boolean;
    monitoring: boolean;
    tracing: boolean;
    multiAi: boolean;
  };
  errors: string[];
  aiProviders: string[];
}

export interface McpCapabilities {
  success: boolean;
  capabilities: {
    chatIntegration: boolean;
    systemMonitoring: boolean;
    jobMonitoring: boolean;
    multiAiOrchestration: boolean;
    toolChainAutomation: boolean;
  };
  mcpProviders: {
    available: string[];
    status: Record<string, boolean>;
  };
  strategies: {
    chat: string[];
    analysis: string[];
    orchestration: string[];
  };
  timestamp: string;
}

export interface McpDashboardData {
  realmId: string;
  timestamp: string;
  systemData: SystemMonitorMcpData;
  jobMetrics: JobMcpMetrics;
  documentMetrics: DocumentJobMcpMetrics;
  summary: {
    totalJobs: number;
    activeJobs: number;
    failedJobs: number;
    documentSuccessRate: number;
    avgProcessingTime: number;
  };
}

// ===== JOB ANALYSIS INTERFACES =====

export interface JobAnalysisRequest {
  jobId: string;
  realmId: string;
  analysisType?: 'simple' | 'standard' | 'comprehensive';
  mcpStrategy?: 'auto' | 'multi_ai' | 'local_only';
}

export interface JobAnalysisResult {
  jobId: string;
  status: 'success' | 'warning' | 'error';
  confidence: number;
  summary: string;
  analysis: {
    rootCause?: string;
    recommendations: string[];
    failureCategory: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    relatedIssues: string[];
    quickFixes: string[];
    preventionSteps: string[];
  };
  orchestration: {
    aiProvidersUsed: string[];
    executionTime: number;
    cost: number;
    mcpStrategy: string;
  };
  // 🚀 NEW: Detailed cost tracking (Phase 3)
  costMetrics?: {
    totalCost: number;
    tokensUsed: number;
    processingTimeMs: number;
    modelUsed: string;
    providerBreakdown: {
      [provider: string]: {
        cost: number;
        tokens: number;
        calls: number;
      };
    };
  };
  timestamp: string;
}

export interface BatchAnalysisRequest {
  jobIds: string[];
  realmId: string;
  analysisType?: 'simple' | 'standard' | 'comprehensive';
  mcpStrategy?: 'auto' | 'multi_ai' | 'local_only';
}

export interface BatchAnalysisResult {
  totalJobs: number;
  analyzedJobs: number;
  failedAnalyses: number;
  results: Record<string, JobAnalysisResult>;
  batchSummary: {
    commonPatterns: string[];
    systemicIssues: string[];
    priorityRecommendations: string[];
  };
  executionTime: number;
  totalCost: number;
  timestamp: string;
}

export interface McpJobData {
  analysis?: JobAnalysisResult;
  enhancedDetails?: any;
  lastAnalyzed?: string;
}

// ===== MCP CHAT INTEGRATION =====

/**
 * Process chat message with MCP orchestration
 */
export async function processChatWithMcp(
  request: ChatMcpRequest, 
  token: string
): Promise<ChatMcpResponse> {
  const res = await fetch('/api/v1/paralex-mcp/chat', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Failed to process chat with MCP' }));
    throw new Error(error.message || 'MCP chat request failed');
  }
  
  const response = await res.json();
  return response.data;
}

// ===== MCP JOB MANAGEMENT =====

/**
 * Get jobs with MCP filtering and enhancement
 */
export async function getJobsWithMcp(params: {
  realmId: string;
  status?: string;
  jobType?: string;
  limit?: number;
  offset?: number;
}, token: string): Promise<JobDetail[]> {
  const url = new URL('/api/v1/paralex-mcp/tools/get_jobs', window.location.origin);
  
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(params)
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch jobs with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Get enhanced job details by ID with MCP analysis
 */
export async function getJobByIdWithMcp(jobId: string, token: string): Promise<JobDetail> {
  const res = await fetch('/api/v1/paralex-mcp/tools/get_job_by_id', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch job details with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Retry job with MCP operation tracking
 */
export async function retryJobWithMcp(jobId: string, realmId: string, token: string): Promise<JobMcpOperationResult> {
  const res = await fetch('/api/v1/paralex-mcp/tools/retry_job', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId, realmId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to retry job with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Remove job with MCP operation tracking
 */
export async function removeJobWithMcp(jobId: string, realmId: string, token: string): Promise<JobMcpOperationResult> {
  const res = await fetch('/api/v1/paralex-mcp/tools/remove_job', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId, realmId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to remove job with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Promote job with MCP operation tracking
 */
export async function promoteJobWithMcp(jobId: string, realmId: string, token: string): Promise<JobMcpOperationResult> {
  const res = await fetch('/api/v1/paralex-mcp/tools/promote_job', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId, realmId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to promote job with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

// ===== MCP MONITORING & ANALYSIS =====

/**
 * Get comprehensive system monitoring data with MCP enhancements
 */
export async function getSystemMonitorWithMcp(realmId: string, token: string): Promise<SystemMonitorMcpData> {
  const res = await fetch(`/api/v1/paralex-mcp/system-monitor?realmId=${realmId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch system monitor data with MCP');
  }
  
  const response = await res.json();
  return response.data;
}

/**
 * Get enhanced job monitoring data with MCP analysis
 */
export async function getJobMonitorWithMcp(jobId: string, token: string): Promise<JobMonitorMcpData> {
  const res = await fetch(`/api/v1/paralex-mcp/job-monitor/${jobId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch job monitor data with MCP');
  }
  
  const response = await res.json();
  return response.data;
}

/**
 * Execute comprehensive job analysis with MCP orchestration
 */
export async function analyzeJobWithMcp(params: {
  jobId: string;
  realmId: string;
  analysisType?: 'simple' | 'standard' | 'comprehensive';
  mcpStrategy?: 'auto' | 'multi_ai' | 'local_only';
}, token: string): Promise<any> {
  const res = await fetch('/api/v1/paralex-mcp/analyze-job', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(params)
  });
  
  if (!res.ok) {
    throw new Error('Failed to analyze job with MCP');
  }
  
  const response = await res.json();
  return response.data;
}

/**
 * Get job metrics with MCP enhancements
 */
export async function getJobMetricsWithMcp(realmId: string, queueName = 'webelieve-jobs', token: string): Promise<JobMcpMetrics> {
  const res = await fetch('/api/v1/paralex-mcp/tools/get_job_metrics', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ realmId, queueName })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch job metrics with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Get document-specific job metrics with MCP analysis
 */
export async function getDocumentJobMetricsWithMcp(realmId: string, token: string): Promise<DocumentJobMcpMetrics> {
  const res = await fetch('/api/v1/paralex-mcp/tools/get_document_job_metrics', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ realmId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch document job metrics with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

// ===== MCP TRACE & ANALYSIS =====

/**
 * Get job trace with MCP enhancements
 */
export async function getJobTraceWithMcp(jobId: string, token: string): Promise<any> {
  const res = await fetch('/api/v1/paralex-mcp/tools/get_job_trace', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch job trace with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Get trace group logs with MCP enhancements
 */
export async function getTraceGroupLogsWithMcp(traceGroupId: string, token: string): Promise<any> {
  const res = await fetch('/api/v1/paralex-mcp/tools/get_trace_group_logs', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ traceGroupId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch trace group logs with MCP');
  }
  
  const response = await res.json();
  return response.result;
}

// ===== MCP HEALTH & CAPABILITIES =====

/**
 * Get MCP health status
 */
export async function getMcpHealthStatus(token: string): Promise<McpHealthStatus> {
  const res = await fetch('/api/v1/paralex-mcp/health', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP health status');
  }
  
  return res.json();
}

/**
 * Get MCP capabilities and available providers
 */
export async function getMcpCapabilities(token: string): Promise<McpCapabilities> {
  const res = await fetch('/api/v1/paralex-mcp/capabilities', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP capabilities');
  }
  
  return res.json();
}

/**
 * Get comprehensive dashboard data optimized for frontend
 */
export async function getMcpDashboardData(realmId: string, token: string): Promise<McpDashboardData> {
  const res = await fetch(`/api/v1/paralex-mcp/dashboard/${realmId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP dashboard data');
  }
  
  return res.json();
}

// ===== JOB ANALYSIS API FUNCTIONS =====

/**
 * Analyze a single failed job using MCP orchestration
 */
export async function analyzeJob(request: JobAnalysisRequest, token: string): Promise<JobAnalysisResult> {
  const res = await fetch('/api/v1/paralex-mcp/jobs/analyze', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  });
  
  if (!res.ok) {
    throw new Error('Failed to analyze job');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Analyze multiple failed jobs in batch using MCP orchestration
 */
export async function analyzeJobsBatch(request: BatchAnalysisRequest, token: string): Promise<BatchAnalysisResult> {
  const res = await fetch('/api/v1/paralex-mcp/jobs/analyze-batch', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  });
  
  if (!res.ok) {
    throw new Error('Failed to analyze jobs in batch');
  }
  
  const response = await res.json();
  // Backend returns results directly in response, not in response.result
  return response;
}

/**
 * Get cached analysis results for jobs
 */
export async function getJobAnalysisResults(jobIds: string[], token: string): Promise<Record<string, JobAnalysisResult>> {
  const res = await fetch('/api/v1/paralex-mcp/jobs/analysis-results', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobIds })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch job analysis results');
  }
  
  const response = await res.json();
  return response.result;
}

/**
 * Get MCP job data including analysis and enhanced details
 */
export async function getMcpJobData(jobIds: string[], token: string): Promise<Record<string, McpJobData>> {
  const res = await fetch('/api/v1/paralex-mcp/jobs/mcp-data', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ jobIds })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP job data');
  }
  
  const response = await res.json();
  return response.result;
}

// ===== MCP COST TRACKING =====

/**
 * Get MCP cost metrics for analysis tracking
 */
export async function getMcpCostMetrics(realmId: string, token: string): Promise<{
  totalAnalysisCost: number;
  analysesPerformed: number;
  avgAnalysisCost: number;
  costToday: number;
  costThisMonth: number;
  mostExpensiveAnalysis?: {
    jobId: string;
    cost: number;
    timestamp: string;
  };
}> {
  const res = await fetch('/api/v1/paralex-mcp/cost-metrics', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ realmId })
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP cost metrics');
  }
  
  const response = await res.json();
  return response.result;
}

// ===== MCP ORCHESTRATION (CONTINUED) =====

/**
 * Execute direct MCP orchestration
 */
export async function executeMcpOrchestration(params: {
  input: any;
  strategy?: string;
  options?: any;
}, token: string): Promise<any> {
  const res = await fetch('/api/v1/paralex-mcp/orchestrate', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(params)
  });
  
  if (!res.ok) {
    throw new Error('Failed to execute MCP orchestration');
  }
  
  return res.json();
}

/**
 * List all available MCP tools
 */
export async function getMcpTools(token: string): Promise<any> {
  const res = await fetch('/api/v1/paralex-mcp/tools', {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch MCP tools');
  }
  
  return res.json();
}

/**
 * Execute specific MCP tool
 */
export async function executeMcpTool(toolName: string, args: any, token: string): Promise<any> {
  const res = await fetch(`/api/v1/paralex-mcp/tools/${toolName}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(args)
  });
  
  if (!res.ok) {
    throw new Error(`Failed to execute MCP tool: ${toolName}`);
  }
  
  return res.json();
}